import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';

const POLICY_URL = process.env.REACT_APP_PRIVACY_POLICY_URL || '';

const sections = [
  {
    title: 'What We Collect',
    body: 'Email address, authentication identifiers, uploaded hair photos, device identifiers, and optional diagnostics analytics.',
  },
  {
    title: 'Why We Collect It',
    body: 'To provide account security, progress tracking, AI analysis, and app reliability.',
  },
  {
    title: 'How We Use Data',
    body: 'We use your data to operate core app features, improve quality, and prevent abuse. We do not sell personal data.',
  },
  {
    title: 'Third-Party Services',
    body: 'We use service providers such as Firebase/Google Cloud and Supabase (where configured) to provide authentication, storage, and infrastructure.',
  },
  {
    title: 'How We Protect Data',
    body: 'Data is encrypted in transit, access is authenticated, and uploads are validated and rate-limited server-side.',
  },
  {
    title: 'Photo Storage and Access',
    body: 'Photos are stored in authenticated user-scoped storage paths and are intended to be private to your account.',
  },
  {
    title: 'Retention',
    body: 'We keep data only as long as needed to provide the service, meet legal requirements, and maintain security.',
  },
  {
    title: 'Deletion Rights',
    body: 'You can delete your account and associated app data in Account settings.',
  },
  {
    title: 'Children\'s Privacy',
    body: 'HairRabbit is not directed to children below the applicable digital consent age in your region.',
  },
  {
    title: 'Contact',
    body: 'Privacy: privacy@HairRabbit.app  Support: support@HairRabbit.app',
  },
];

export default function PrivacyConsent() {
  const { idToken, fetchUserProfile, signOut } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canOpenPolicy = useMemo(() => /^https?:\/\//i.test(POLICY_URL), []);

  const handleAccept = async () => {
    if (!accepted || saving) return;

    setSaving(true);
    setError('');
    try {
      await apiService.acceptPrivacyPolicy();
      await fetchUserProfile(idToken);
    } catch (e) {
      setError('Could not save your consent right now. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDecline = async () => {
    await signOut();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--background)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '760px',
        border: '1px solid var(--border)',
        borderRadius: '16px',
        background: 'var(--card)',
        padding: '24px',
      }}>
        <h1 style={{ margin: '0 0 10px', fontSize: '1.9rem' }}>Privacy Policy</h1>
        <p style={{ margin: '0 0 20px', color: 'var(--muted-foreground)', lineHeight: 1.6 }}>
          Before continuing, review and accept our Privacy Policy. New accounts must accept this policy to access HairRabbit.
        </p>

        <div style={{ display: 'grid', gap: '14px' }}>
          {sections.map((section) => (
            <div key={section.title}>
              <div style={{ fontWeight: 700, marginBottom: '4px' }}>{section.title}</div>
              <div style={{ color: 'var(--muted-foreground)', lineHeight: 1.6 }}>{section.body}</div>
            </div>
          ))}
        </div>

        {canOpenPolicy ? (
          <p style={{ marginTop: '14px' }}>
            <a href={POLICY_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>
              Read Full Privacy Policy
            </a>
          </p>
        ) : null}

        <label style={{
          marginTop: '18px',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          color: 'var(--foreground)',
          lineHeight: 1.6,
        }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            style={{ marginTop: '4px' }}
          />
          <span>I have read and accept the Privacy Policy.</span>
        </label>

        {error ? (
          <p style={{ color: '#b91c1c', marginTop: '12px', marginBottom: 0 }}>{error}</p>
        ) : null}

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button
            type="button"
            onClick={handleDecline}
            style={{
              flex: 1,
              borderRadius: '10px',
              border: '1px solid var(--border)',
              background: 'transparent',
              padding: '12px 14px',
              cursor: 'pointer',
            }}
          >
            Decline
          </button>
          <button
            type="button"
            disabled={!accepted || saving}
            onClick={handleAccept}
            style={{
              flex: 1.5,
              borderRadius: '10px',
              border: 'none',
              background: 'var(--foreground)',
              color: 'var(--primary-foreground)',
              padding: '12px 14px',
              cursor: !accepted || saving ? 'not-allowed' : 'pointer',
              opacity: !accepted || saving ? 0.6 : 1,
              fontWeight: 700,
            }}
          >
            {saving ? 'Saving...' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
